// Einstellungen → Logo (#1989): a business admin sets the logo its console header shows in place
// of the product mark. The stored logo arrives with the session configuration; this screen
// only uploads, replaces and removes it, and previews the header slot exactly as the shell
// renders it.
import React from "react";
import { Banner, Button, Card, StatusPill } from "@/ds";
import { PanelLabel } from "@/lib";
import { ApiError, cockpit, getTenantContext, withTenantContext } from "@/api";
import type { BusinessLogo } from "@/api";
import type { PushToast } from "@/notifications/toast-queue";
import { useT } from "../i18n";
import { RibbonMark } from "../brand";
import { BusinessLogoMark } from "../shell-sidebar";
import styles from "./settings-logo.module.css";

const LOGO_ACCEPT = "image/png,image/jpeg";

export function BusinessLogoCard({ logo, onChanged, pushToast }: {
  /** What the session configuration says is stored; null without a logo. */
  logo: BusinessLogo | null;
  /** Re-reads the configuration, which is what the header itself renders from. */
  onChanged: (options?: { background?: boolean }) => Promise<void>;
  pushToast: PushToast;
}) {
  const t = useT();
  const fileInput = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = React.useState<"upload" | "remove" | null>(null);
  const [problem, setProblem] = React.useState<"invalid" | "failed" | null>(null);
  // The server's answer to this screen's own change is shown at once; the configuration follows
  // a moment later and takes over again as soon as it reports anything new.
  const [answered, setAnswered] = React.useState<{ logo: BusinessLogo | null } | null>(null);
  React.useEffect(() => { setAnswered(null); }, [logo?.id]);
  const shown = answered ? answered.logo : logo;
  // The store accepts an image by its structure; a browser can still fail to draw one. The
  // preview then does what the header does and says so, instead of showing an empty plate.
  const [unreadableId, setUnreadableId] = React.useState<string | null>(null);
  const drawn = shown && shown.id !== unreadableId ? shown : null;
  // "Remove" leaves the page with the logo it removed; the focus moves on to the action that is
  // left instead of falling back to the document.
  const actions = React.useRef<HTMLDivElement | null>(null);
  const focusUpload = React.useRef(false);
  React.useEffect(() => {
    if (!focusUpload.current || shown) return;
    focusUpload.current = false;
    actions.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [shown]);

  const apply = (action: "upload" | "remove", run: () => Promise<{ logo: BusinessLogo | null }>) => {
    // #2111: an answer that arrives after the owner entered another business belongs to the one
    // that was left. Its toast would read as a change to the business now open, and the re-read
    // would be of that one.
    const business = getTenantContext();
    const left = () => getTenantContext() !== business;
    setBusy(action);
    setProblem(null);
    run()
      .then((state) => {
        if (left()) return;
        focusUpload.current = action === "remove";
        setAnswered(state);
        pushToast({ tone: "success", msg: t(state.logo ? "settings.logo.saved" : "settings.logo.removed") });
        // The header reads the configuration. In the background, so that a failed re-read
        // leaves the shell as it is; the live refresh of settings brings the change in.
        void onChanged({ background: true }).catch(() => undefined);
      })
      .catch((error: unknown) => {
        if (left()) return;
        setProblem(error instanceof ApiError && error.status === 400 ? "invalid" : "failed");
      })
      .finally(() => setBusy(null));
  };

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so that choosing the same file again after a refusal is a change once more.
    event.target.value = "";
    if (file) apply("upload", () => cockpit.setBusinessLogo(file));
  };

  return (
    <Card className="management-panel" data-testid="business-logo-card">
      <PanelLabel icon="building" action={
        <StatusPill tone={shown ? "success" : "neutral"}>
          {t(shown ? "settings.logo.statusOwn" : "settings.logo.statusDefault")}
        </StatusPill>
      }>
        {t("settings.logo.title")}
      </PanelLabel>
      <p className="management-copy">{t("settings.logo.hint")}</p>

      <figure className={styles.preview}>
        <figcaption className={styles.caption}>{t("settings.logo.preview")}</figcaption>
        <div className={styles.bar} data-ribbon-theme="dark" data-testid="business-logo-preview">
          <div className={styles.slot}>
            {drawn
              ? <BusinessLogoMark logo={{ url: withTenantContext(drawn.url), width: drawn.width, height: drawn.height }}
                  onError={() => setUnreadableId(drawn.id)} />
              : <RibbonMark decorative />}
          </div>
        </div>
      </figure>

      {shown && !drawn && (
        <Banner tone="warning" title={t("settings.logo.unreadableTitle")}>{t("settings.logo.unreadable")}</Banner>
      )}
      {problem && (
        <Banner tone="danger" title={t("common.error")}>
          {t(problem === "invalid" ? "settings.logo.invalid" : "settings.logo.failed")}
        </Banner>
      )}

      <div className={styles.footer}>
        <p className={styles.requirements}>{t("settings.logo.requirements")}</p>
        <div className={styles.actions} ref={actions}>
          <input ref={fileInput} type="file" className="sr-only" tabIndex={-1} aria-hidden="true"
            accept={LOGO_ACCEPT} onChange={onFile} data-testid="business-logo-file" />
          {shown && (
            <Button variant="secondary" onClick={() => apply("remove", () => cockpit.clearBusinessLogo())}
              loading={busy === "remove"} disabled={busy !== null} data-testid="business-logo-remove">
              {t("settings.logo.remove")}
            </Button>
          )}
          <Button onClick={() => fileInput.current?.click()} loading={busy === "upload"} disabled={busy !== null}
            data-testid="business-logo-upload">
            {t(shown ? "settings.logo.replace" : "settings.logo.upload")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
